import { Bid, BidTabsData, ScopeUseGroup } from "../../interfaces/BidTabs";

/** A bid's cell on a scope row, as every `scope_*_bid` table stores it. */
interface ScopeCell {
  bid: string;
  value: string | null;
  type?: string | null;
  destination_bid_group?: string | null;
  deleted_at?: string | null;
}

/** The cell each bid holds on one row, keyed by bid id, then by row id. */
type CellsByRow = Record<string, Record<string, ScopeCell>>;

/** A scope row that can send a share of its bid cells to use groups. */
interface AllocatingRow {
  id: string;
  project: string;
  bid_group?: string | null;
  scope_use_groups: Record<string, number> | string | null;
  accepted?: boolean;
  tags?: unknown;
  deleted_at?: string | null;
}

/** A row joined with its cells. */
interface RowWithCells {
  row: AllocatingRow;
  cells: Record<string, ScopeCell>;
}

/** The bid of a bid group whose totals the SOV reports, as the bid group's stats carry it. */
export interface SelectedBidder {
  id: string;
  is_carry: boolean;
  grand_total: number;
}

/** The cell types that let a row send its value to a use group. */
const ALLOCATING_CELL_TYPES = ["BP", "SP"];

/** The `tags` value marking a row the project accepted. */
const ACCEPTED_TAG = "ACCEPTED";

const indexCells = <Cell extends ScopeCell>(cells: Cell[], rowOf: (cell: Cell) => string): CellsByRow => {
  const byRow: CellsByRow = {};
  for (const cell of cells) {
    if (cell.deleted_at) continue;
    const rowId = rowOf(cell);
    if (!byRow[rowId]) byRow[rowId] = {};
    byRow[rowId][cell.bid] = cell;
  }
  return byRow;
};

const joinRows = <Row extends AllocatingRow>(rows: Row[], cellsByRow: CellsByRow): RowWithCells[] =>
  rows.filter((row) => !row.deleted_at).map((row) => ({ row, cells: cellsByRow[row.id] || {} }));

/** The number a cell reads as: commas stripped, an empty cell 0, a non-numeric cell NaN. */
const cellNumber = (value: string | null | undefined): number =>
  value ? parseFloat(String(value).replace(/,/g, "")) : 0;

/** A No With cell holds the bid group it points at, not an amount. */
const cellAmount = (cell: ScopeCell | undefined): number => (cell?.type === "NO_WITH" ? NaN : cellNumber(cell?.value));

/** The percentage the row allocates to the use group; the column holds a map keyed by use group. */
const allocationOf = (row: AllocatingRow, useGroupId: string): number | undefined => {
  const allocations = row.scope_use_groups;
  if (!allocations || typeof allocations === "string") return undefined;
  return allocations[useGroupId];
};

const isAccepted = (row: AllocatingRow): boolean => Boolean(row.accepted) || row.tags === ACCEPTED_TAG;

const ofBidGroup = <Row extends AllocatingRow>(rows: Row[], bidGroupId: string): Row[] =>
  rows.filter((row) => row.bid_group === bidGroupId);

const ofProjectOrBidGroup = <Row extends AllocatingRow>(rows: Row[], projectId: string, bidGroupId: string): Row[] =>
  rows.filter((row) => row.project === projectId && (!row.bid_group || row.bid_group === bidGroupId));

/**
 * The bid group's section items: those in one of its sections, plus items of another bid group
 * whose selected bid marked them No With this bid group.
 */
const sectionItemsOfBidGroup = (
  scopeData: BidTabsData,
  bidGroupId: string,
  projectId: string,
  sectionItemCells: CellsByRow
): BidTabsData["scope_section_items"] => {
  const sections = new Set(
    (scopeData.scope_sections || [])
      .filter((section) => section.bid_group === bidGroupId && !section.deleted_at)
      .map((section) => section.id)
  );
  const projectBidGroups = new Set(
    (scopeData.bid_groups || [])
      .filter(
        (bidGroup) => bidGroup.project === projectId && !bidGroup.deleted_at && !bidGroup.qual && !bidGroup.frontend
      )
      .map((bidGroup) => bidGroup.id)
  );
  const selectedBidByBidGroup: Record<string, string> = {};
  for (const bid of scopeData.bids || []) {
    if (bid.selected && !bid.deleted_at) selectedBidByBidGroup[bid.bid_group] = bid.id;
  }

  const items = (scopeData.scope_section_items || []).filter((item) => !item.deleted_at);
  const inSections = items.filter((item) => sections.has(item.scope_section));
  const movedIn = items.filter((item) => {
    if (item.bid_group === bidGroupId || !projectBidGroups.has(item.bid_group)) return false;
    const cell = sectionItemCells[item.id]?.[selectedBidByBidGroup[item.bid_group]];
    return cell?.type === "NO_WITH" && cell.destination_bid_group === bidGroupId;
  });
  return [...inSections, ...movedIn];
};

/** Every row of the project that can allocate to a use group, with its cells, for one bid group. */
const allocatingRows = (scopeData: BidTabsData, bidGroupId: string, projectId: string): RowWithCells[] => {
  const sectionItemCells = indexCells(scopeData.scope_section_item_bid || [], (cell) => cell.scope_section_item);

  const joined = [
    ...joinRows(
      (scopeData.scope_alternates || []).filter((row) => row.project === projectId),
      indexCells(scopeData.scope_alternate_bid || [], (cell) => cell.scope_alternate)
    ),
    ...joinRows(
      ofBidGroup(scopeData.scope_attributes || [], bidGroupId),
      indexCells(scopeData.scope_attribute_bid || [], (cell) => cell.scope_attribute)
    ),
    ...joinRows(
      ofBidGroup(scopeData.scope_trade_group_breakouts || [], bidGroupId),
      indexCells(scopeData.scope_trade_group_breakout_bid || [], (cell) => cell.scope_trade_group_breakout)
    ),
    ...joinRows(
      ofProjectOrBidGroup(scopeData.scope_bid_documents || [], projectId, bidGroupId),
      indexCells(scopeData.scope_bid_document_bid || [], (cell) => cell.scope_bid_document)
    ),
    ...joinRows(
      ofProjectOrBidGroup(scopeData.scope_general_requirements || [], projectId, bidGroupId),
      indexCells(scopeData.scope_general_requirement_bid || [], (cell) => cell.scope_general_requirement)
    ),
    ...joinRows(sectionItemsOfBidGroup(scopeData, bidGroupId, projectId, sectionItemCells), sectionItemCells),
    ...joinRows(
      ofBidGroup(scopeData.spec_sections || [], bidGroupId),
      indexCells(scopeData.scope_spec_section_bid || [], (cell) => cell.spec_section)
    ),
  ];

  return joined.filter(({ row }) => row.project === projectId);
};

/**
 * The trade cleanup amount the bidder's use groups share: the Carry row's value for the carry bid,
 * and for every other bid the carry bid's value less the bid's own, as the scope sheet's
 * Adjustment row shows it. NaN when a cell is not a number.
 */
const tradeCleanupOf = (
  scopeData: BidTabsData,
  bidGroupId: string,
  projectId: string,
  bidder: SelectedBidder
): number => {
  const cleanup = (scopeData.scope_trade_cleanups || []).find((row) => row.project === projectId && !row.deleted_at);
  if (!cleanup) return 0;
  const cells =
    indexCells(scopeData.scope_trade_cleanup_bid || [], (cell) => cell.scope_trade_cleanup)[cleanup.id] || {};

  const carryBid = (scopeData.bids || []).find(
    (bid) => bid.bid_group === bidGroupId && bid.is_carry && !bid.deleted_at
  );
  const carryValue = cellNumber(cells[carryBid?.id ?? ""]?.value);
  if (bidder.is_carry) return carryValue;
  return carryValue - cellNumber(cells[bidder.id]?.value);
};

/**
 * What each of the project's use groups holds for a bid group's selected bidder, as the web app's
 * bid groups grid reports it: the bidder's cell on the use group row, plus every related row's
 * cell scaled by the percentage that row allocates to the use group when the cell is a bid plug
 * (BP), a subcontractor plug (SP) or sits on an accepted alternate, plus the trade cleanup
 * adjustment scaled by the bidder's cell-over-base-bid percent. With a single use group on the
 * project the bidder's grand total is the whole allocation.
 *
 * @returns the total per scope use group id; a use group with nothing allocated is left out.
 */
export const computeUseGroupTotals = (
  scopeData: BidTabsData,
  bidGroup: { id: string; project: string },
  bidder: SelectedBidder
): Record<string, number> => {
  const useGroups: ScopeUseGroup[] = (scopeData.scope_use_groups || []).filter(
    (useGroup) => useGroup.project === bidGroup.project
  );
  if (useGroups.length === 0) return {};

  const totals: Record<string, number> = {};
  if (useGroups.length === 1) {
    if (bidder.grand_total > 0) totals[useGroups[0].id] = bidder.grand_total;
    return totals;
  }

  const bid: Bid | undefined = (scopeData.bids || []).find((row) => row.id === bidder.id);
  const baseBid = bid?.base_bid || 0;
  const useGroupCells = indexCells(scopeData.scope_use_group_bid || [], (cell) => cell.scope_use_group);
  const rows = allocatingRows(scopeData, bidGroup.id, bidGroup.project);
  const tradeCleanup = tradeCleanupOf(scopeData, bidGroup.id, bidGroup.project, bidder);

  for (const useGroup of useGroups) {
    const own = cellNumber(useGroupCells[useGroup.id]?.[bidder.id]?.value);
    let value = isFinite(own) ? own : 0;
    const percentOfBaseBid = baseBid > 0 && value > 0 ? (value * 100) / baseBid : 0;

    for (const { row, cells } of rows) {
      const allocated = allocationOf(row, useGroup.id);
      if (!allocated) continue;
      const cell = cells[bidder.id];
      const amount = cellAmount(cell);
      const allowed = ALLOCATING_CELL_TYPES.includes(cell?.type ?? "") || isAccepted(row);
      if (isFinite(amount) && allowed) value += (amount * allocated) / 100;
    }

    if (isFinite(tradeCleanup)) value += (tradeCleanup * percentOfBaseBid) / 100;

    if (value) totals[useGroup.id] = value;
  }
  return totals;
};
